import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";
import { Activity, HeartPulse, Stethoscope, CalendarCheck } from "lucide-react";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const TYPE_COLORS = { Abdominal: "#7FD498", Ecocardio: "#9DC3E6", Otros: "#FBBC0C" };

function categorize(study) {
  const s = (study || "").toLowerCase();
  if (s.includes("abdominal")) return "Abdominal";
  if (s.includes("card")) return "Ecocardio";
  return "Otros";
}

export function ClinicStats({ bookings }) {
  const data = useMemo(() => {
    const active = bookings.filter((b) => b.status !== "cancelled");
    const byType = { Abdominal: 0, Ecocardio: 0, Otros: 0 };
    active.forEach((b) => { byType[categorize(b.study_type)] += 1; });

    // last 6 months
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: MONTHS[d.getMonth()], count: 0 });
    }
    const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
    active.forEach((b) => {
      const key = (b.slot_date || "").slice(0, 7);
      if (key in idx) months[idx[key]].count += 1;
    });

    const pie = Object.entries(byType).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
    return { byType, months, pie, total: active.length };
  }, [bookings]);

  const cards = [
    { key: "Abdominal", label: "Abdominal", icon: Activity, color: "#7FD498" },
    { key: "Ecocardio", label: "Ecocardio", icon: HeartPulse, color: "#9DC3E6" },
    { key: "Otros", label: "Otras", icon: Stethoscope, color: "#FBBC0C" },
  ];

  return (
    <div className="mb-6 space-y-4" data-testid="clinic-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="bg-[#373E45] rounded-2xl p-5 text-white" data-testid="stat-total">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-300">Total ecografías</p>
            <CalendarCheck className="h-5 w-5 text-[#7FD498]" />
          </div>
          <p className="text-3xl font-extrabold mt-2">{data.total}</p>
        </motion.div>
        {cards.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 * (i + 1) }}
            className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`stat-type-${c.key}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-[#64748B]">{c.label}</p>
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${c.color}25` }}>
                <c.icon className="h-5 w-5" style={{ color: c.color }} />
              </div>
            </div>
            <p className="text-3xl font-extrabold text-[#373E45] mt-2">{data.byType[c.key]}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-[#373E45] mb-1">Evolución mensual</h3>
          <p className="text-sm text-[#64748B] mb-4">Ecografías realizadas en los últimos 6 meses.</p>
          <div className="h-64" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.months} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#7FD49815" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontWeight: 600 }} labelStyle={{ color: "#373E45" }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]} fill="#7FD498" isAnimationActive animationDuration={1400} animationBegin={200} name="Ecografías" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-[#373E45] mb-1">Por tipo de estudio</h3>
          <p className="text-sm text-[#64748B] mb-4">Distribución de tus ecografías.</p>
          {data.pie.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">Aún no hay datos.</p>
          ) : (
            <div className="h-56" data-testid="type-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontWeight: 600 }} />
                  <Pie data={data.pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={3}
                    isAnimationActive animationDuration={1200} animationBegin={300} stroke="none">
                    {data.pie.map((e) => <Cell key={e.name} fill={TYPE_COLORS[e.name]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {Object.entries(TYPE_COLORS).map(([name, color]) => (
              <span key={name} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B]">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{name}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
